import { Component, EventEmitter, OnInit, OnDestroy, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { LancamentosDeCaixaService } from '../../../services/lancamentos-de-caixa.service';
import { PlanoDeContasService } from '../../../services/plano-de-contas.service';
import { SpinnerService } from '../../../services/spinner.service';
import { ToastService } from '../../../services/toast.service';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs';
import { SeveridadeToast } from '../../../models/enums/severidade-toast.enum';
import { Icones } from '../../../shared/variaveisDeRotas';
import { JwtHelper } from '../../../helpers/jwtDecode.helper';

export enum LancamentoCaixaEnum {
  Recebimento = 0,
  Pagamento = 1,
}

@Component({
  selector: 'app-detalhes-lancamento-caixa',
  templateUrl: './detalhes-lancamento-caixa.component.html',
  styleUrls: ['./detalhes-lancamento-caixa.component.scss']
})
export class DetalhesLancamentoCaixaComponent implements OnInit, OnDestroy {
  @Output() tituloChange = new EventEmitter<string>();
  @Output() iconeChange = new EventEmitter<string>();

  lancamentoFormEditar: FormGroup = new FormGroup({
    id: new FormControl(''),
    tipo: new FormControl(0, Validators.required),
    periodo: new FormControl<Date | null>(null, Validators.required),
    valor: new FormControl(null, [Validators.required, Validators.min(0)]),
    descricao: new FormControl('', Validators.required),
    contaContabilId: new FormControl(null, Validators.required),
    usuarioId: new FormControl(''),
  });

  tipoOptions = [
    { label: 'Recebimento', value: LancamentoCaixaEnum.Recebimento },
    { label: 'Pagamento', value: LancamentoCaixaEnum.Pagamento }
  ];

  contaContabilOptions: any[] = [];
  userId: string;

  constructor(
    private route: ActivatedRoute,
    private lancamentoDeCaixaService: LancamentosDeCaixaService,
    private planoDeContasService: PlanoDeContasService,
    private spinnerService: SpinnerService,
    private jwtHelper: JwtHelper,
    private toastService: ToastService,
    private router: Router,
  ) {
    this.userId = this.jwtHelper.getUserIdFromToken();
  }

  ngOnInit(): void {
    this.lancamentoFormEditar.disable();
    this.tituloChange.emit('Detalhes Lançamento');
    this.iconeChange.emit(Icones.pranchetaAmarelo);
    this.preencheCamposPorURL();
  }

  ngOnDestroy(): void {
    this.spinnerService.hide();
  }

  onTipoChange(event: any): void {
    const tipo = event.value;
    this.listaPorTipoLancamento(tipo);
  }

  listaPorTipoLancamento(tipo: number): void {
    this.spinnerService.show('Carregando...');
    this.lancamentoDeCaixaService.listaTipoLancamentoPreenchido(tipo)
      .pipe(finalize(() => this.spinnerService.hide()))
      .subscribe({
        next: (data: any[]) => {
          const contaContabilIds = data.map(item => item.id);
          if (contaContabilIds.length > 0) {
            this.carregaPlanoDeContas(contaContabilIds);
          } else {
            this.toastService.setMensagemToast('Nenhuma conta contábil encontrada.', SeveridadeToast.Warn);
          }
        },
        error: () => {
          this.toastService.setMensagemToast('Erro ao carregar Lançamento caixa.', SeveridadeToast.Error);
        }
      });
  }

  carregaPlanoDeContas(contaContabilIds: string[]): void {
    this.planoDeContasService.getContasContabeis(this.userId).subscribe({
      next: (res: any[]) => {
        this.contaContabilOptions = res.filter(contaContabil =>
          contaContabilIds.includes(contaContabil.id)
        );
        this.selecionaContaContabil();
      },
      error: () => {
        this.toastService.setMensagemToast('Erro ao carregar plano de contas', SeveridadeToast.Error);
      }
    });
  }

  preencheCamposPorURL(): void {
    this.route.params.subscribe(params => {
      this.lancamentoFormEditar.patchValue({
        id: params['id'],
        tipo: +params['tipo'],
        periodo: this.transformaDataParaDate(params['periodo']),
        valor: +params['valor'],
        descricao: params['descricao'],
        contaContabilId: params['contaContabilId'],
        usuarioId: this.userId
      });
      this.listaPorTipoLancamento(+params['tipo']);
    });
  }

  selecionaContaContabil(): void {
    const contaContabilId = this.lancamentoFormEditar.get('contaContabilId')?.value;
    if (contaContabilId) {
      const contaContabil = this.contaContabilOptions.find(conta => conta.id === contaContabilId);
      if (contaContabil) {
        this.lancamentoFormEditar.patchValue({ contaContabilId: contaContabil });
      }
    }
  }

  onSubmit(): void {
    if (this.lancamentoFormEditar.valid) {
      const formValue = this.lancamentoFormEditar.value;
      const lancamentoData = {
        ...formValue,
        contaContabilId: formValue.contaContabilId.id,
        periodo: this.transformaDataEnvio(formValue.periodo),
      };

      this.spinnerService.show('Salvando...');
      this.lancamentoDeCaixaService.editarLancamentoCaixa(lancamentoData).subscribe({
        next: () => {
          this.toastService.setMensagemToast('Lançamento caixa editado com sucesso!', SeveridadeToast.Success);
          this.spinnerService.hide();
          this.router.navigate(['listar-lancamento-caixa']);
        },
        error: (e) => {
          this.toastService.setMensagemToast(`Erro ao editar lançamento caixa: ${e.error}`, SeveridadeToast.Error);
          this.spinnerService.hide();
        }
      });
    }
  }

  transformaDataEnvio(data: Date | null): number {
    if (!data) {
      throw new Error('Data é obrigatória');
    }
    
    const ano = data.getFullYear();
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const dia = String(data.getDate()).padStart(2, '0');
    
    return Number(`${ano}${mes}${dia}`);
  }

  transformaDataParaDate(data: number): Date {
    const dataString = data.toString();
    const ano = parseInt(dataString.substring(0, 4));
    const mes = parseInt(dataString.substring(4, 6)) - 1;
    const dia = parseInt(dataString.substring(6, 8));
    return new Date(ano, mes, dia);
  }

  transformaData(data: number): string {
    const dataString = data.toString();
    const ano = dataString.substring(0, 4);
    const mes = dataString.substring(4, 6);
    const dia = dataString.substring(6, 8);
    return `${ano}-${mes}-${dia}`;
  }

  retornar(): void {
    this.spinnerService.hide();
    this.router.navigate(['listar-lancamento-caixa']);
  }
}
